import React, { useState, useEffect } from 'react';
import { getGruposEstudiante, getEstudiantesGrupo } from '../../api/estudiantesGrupos';
import { getGroups } from '../../api/groups';
import styles from '../../styles/MateriasStyles/PantallaDeTareas.v2.module.css';
import Pagination from '../../components/Pagination.tsx';

const MAX_ALUMNOS = 4;
const PAGE_SIZE = 4;


const CardGruposEstudiantesModal = ({ idEstudiante, query = '' }) => {
  const [modalGrupo, setModalGrupo] = useState(null);
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1); // Resetear página cuando cambian los grupos
    setLoading(true);
    // Consumir el endpoint para obtener los grupos del estudiante
    if (idEstudiante) {
      getGruposEstudiante(idEstudiante)
        .then(async data => {
          const gruposConAlumnos = await Promise.all(
            data.map(async item => {
              let alumnos = [];
              if (item.id_grupo) {
                try {
                  alumnos = await getEstudiantesGrupo(item.id_grupo);
                } catch (e) {
                  alumnos = [];
                }
              }
              return {
                id: item.id_grupo,
                nombre: item.grupo_codigo || item.nombre || '',
                modalidad: item.grupo_modalidad || '',
                asesor: item.asesor_nombre || '',
                horario: item.horario_texto || '',
                aula: '',
                dias: '',
                horarios: item.horarios || [],
                alumnos,
              };
            })
          );
          setGrupos(gruposConAlumnos);
        })
        .finally(() => setLoading(false));
    } else {
      // Mostrar todos los grupos
      getGroups()
        .then(async data => {
          const gruposRaw = data.results || data;
          const gruposConAlumnos = await Promise.all(
            gruposRaw.map(async item => {
              let alumnos = [];
              if (item.id_grupo) {
                try {
                  alumnos = await getEstudiantesGrupo(item.id_grupo);
                } catch (e) {
                  alumnos = [];
                }
              }
              return {
                id: item.id_grupo,
                nombre: item.nombre || item.grupo_codigo || '',
                modalidad: item.modalidad || '',
                asesor: item.asesor_nombre || '',
                horario: '',
                aula: item.aula || '',
                dias: '',
                horarios: [],
                alumnos,
              };
            })
          );
          setGrupos(gruposConAlumnos);
        })
        .finally(() => setLoading(false));
    }
  }, [idEstudiante]);

  const handleOpenModal = (grupo) => {
    setModalGrupo(grupo);
  };
  const handleCloseModal = () => {
    setModalGrupo(null);
  };

  if (loading) return <div style={{ padding: 32, textAlign: 'center' }}>Cargando grupos...</div>;


  // FILTRADO LOCAL
  const normalizedQuery = query.trim().toLowerCase();
  const gruposFiltrados = !normalizedQuery
    ? grupos
    : grupos.filter(grupo => {
        // Código de grupo
        const codigoGrupo = (grupo.nombre || '').toLowerCase();
        // Asesor
        const asesor = (grupo.asesor || '').toLowerCase();
        // Alumnos
        const alumnos = (grupo.alumnos || []);
        const alumnoMatch = alumnos.some(alumno => {
          const nombre = (alumno.nombre_completo || alumno.nombre || '').toLowerCase();
          return nombre.includes(normalizedQuery);
        });
        return (
          codigoGrupo.includes(normalizedQuery) ||
          asesor.includes(normalizedQuery) ||
          alumnoMatch
        );
      });

  // PAGINACIÓN
  const totalPages = Math.max(1, Math.ceil(gruposFiltrados.length / PAGE_SIZE));
  const paginatedGrupos = gruposFiltrados.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', justifyContent: 'flex-start' }}>
        {paginatedGrupos.map(grupo => {
          const alumnosMostrados = grupo.alumnos.slice(0, MAX_ALUMNOS);
          const alumnosOcultos = grupo.alumnos.length - MAX_ALUMNOS;
          // Modern UI: datos en bloques, días en círculos
          const diasSemana = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
          // Si grupo.dias es una lista de números (ej: '1,2,3'), convertir a letras
          let diasActivos = [];
          if (grupo.dias) {
            const mapNumToDia = {
              '1': 'D', // Domingo
              '2': 'L', // Lunes
              '3': 'M', // Martes
              '4': 'M', // Miércoles
              '5': 'J', // Jueves
              '6': 'V', // Viernes
              '7': 'S', // Sábado
            };
            diasActivos = grupo.dias.split(',').map(num => mapNumToDia[num.trim()]).filter(Boolean);
          }
          return (
            <div
              key={grupo.id}
              className={styles.cardGrupo}
              style={{
                background: 'linear-gradient(180deg, #f8fafc 80%, #fff 100%)',
                borderRadius: 22,
                boxShadow: '0 6px 32px #0001',
                padding: '32px 28px 24px 28px',
                minWidth: 300,
                maxWidth: 370,
                flex: '1 1 300px',
                margin: '18px 0',
                transition: 'box-shadow 0.2s',
                fontFamily: 'SF Pro Display, Inter, Arial, sans-serif',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 18 }}>
                <div style={{ width: 54, height: 54, borderRadius: '50%', background: grupo.color || '#0a3564', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 28, boxShadow: '0 2px 8px #0002', marginBottom: 10 }}>
                  {/* Icono minimalista, puedes cambiar por SVG si lo deseas */}
                  <span style={{ fontWeight: 700, fontSize: 28 }}>{grupo.nombre ? grupo.nombre.trim().charAt(0) : 'G'}</span>
                </div>
                <div style={{ textAlign: 'center', width: '100%' }}>
                  <div style={{ fontWeight: 700, fontSize: 22, color: '#0a3564', marginBottom: 2, letterSpacing: 0.2 }}>{grupo.nombre}</div>
                  <div style={{ fontSize: 15, color: '#888', marginBottom: 8, fontWeight: 500 }}>{grupo.alumnos.length} alumno{grupo.alumnos.length === 1 ? '' : 's'}</div>
                </div>
              </div>
              {/* Asesor en línea aparte */}
              {grupo.asesor && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 14, color: '#0a3564', background: '#eaf1fb', borderRadius: 10, padding: '5px 14px', fontWeight: 500, boxShadow: '0 1px 4px #0001' }}>
                    <b>Asesor:</b> {grupo.asesor}
                  </span>
                </div>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 10, justifyContent: 'center' }}>
                {grupo.modalidad && (
                  <span style={{ fontSize: 14, color: '#0a3564', background: '#f5f7fa', borderRadius: 10, padding: '5px 14px', fontWeight: 500, boxShadow: '0 1px 4px #0001' }}>
                    <b>Modalidad:</b> {grupo.modalidad}
                  </span>
                )}
                {grupo.horario && ['Matutino','Vespertino','Nocturno'].includes(grupo.horario) && (
                  <span style={{ fontSize: 14, color: '#0a3564', background: '#f5f7fa', borderRadius: 10, padding: '5px 14px', fontWeight: 500, boxShadow: '0 1px 4px #0001' }}>
                    {grupo.horario}
                  </span>
                )}
                {/* Mostrar todos los horarios del grupo */}
                {grupo.horarios && grupo.horarios.length > 0 && (
                  <span style={{ fontSize: 14, color: '#0a3564', background: '#f5f7fa', borderRadius: 10, padding: '5px 14px', fontWeight: 500, boxShadow: '0 1px 4px #0001', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <b>Horarios:</b>
                    {grupo.horarios.map((h, idx) => (
                      <span key={idx} style={{ fontWeight: 400 }}>
                        {(() => {
                          // Formatear hora a HH:mm
                          const formatHora = (hora) => hora ? hora.slice(0,5) : '';
                          return `${formatHora(h.hora_inicio)} - ${formatHora(h.hora_fin)}`;
                        })()}
                      </span>
                    ))}
                  </span>
                )}
                {grupo.aula && (
                  <span style={{ fontSize: 14, color: '#0a3564', background: '#f5f7fa', borderRadius: 10, padding: '5px 14px', fontWeight: 500, boxShadow: '0 1px 4px #0001' }}>
                    <b>Aula:</b> {grupo.aula}
                  </span>
                )}
              </div>
              {diasActivos.length > 0 && (
                <div style={{ display: 'flex', gap: 10, marginTop: 12, marginBottom: 8, justifyContent: 'center' }}>
                  {diasSemana.map((dia, idx) => (
                    <span key={dia + idx} style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      background: diasActivos.includes(dia) ? '#0a3564' : '#eaf1fb',
                      color: diasActivos.includes(dia) ? '#fff' : '#888',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 12,
                      boxShadow: 'none',
                      transition: 'background 0.2s',
                    }}>{dia}</span>
                  ))}
                </div>
              )}
              <div style={{ height: 1, background: '#eaf1fb', margin: '18px 0 16px 0', borderRadius: 2 }} />
              <div style={{ fontSize: 15, fontWeight: 600, color: '#0a3564', marginBottom: 8, letterSpacing: 0.1 }}>Listado de alumnos:</div>
              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {alumnosMostrados.map(alumno => (
                    <div key={alumno.id} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f5f7fa', borderRadius: 8, padding: '4px 10px', fontSize: 13 }}>
                      {alumno.avatar ? (
                        <img src={alumno.avatar} alt={alumno.nombre} style={{ width: 22, height: 22, borderRadius: '50%' }} />
                      ) : (
                        <span style={{ fontSize: 16 }}>👤</span>
                      )}
                      <span>{alumno.nombre_completo || alumno.nombre}</span>
                        <span>{(alumno.nombre && alumno.nombre !== 'N/A') ? alumno.nombre : (alumno.nombre_completo && alumno.nombre_completo !== 'N/A' ? alumno.nombre_completo : '')}</span>
                      </div>
                    ))}
                  {alumnosOcultos > 0 && (
                    <button
                      style={{ background: '#eaf1fb', color: '#0a3564', border: 'none', borderRadius: 8, padding: '4px 10px', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
                      onClick={() => handleOpenModal(grupo)}
                    >
                      +{alumnosOcultos} más
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Paginación de tarjetas de grupos */}
      <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center' }}>
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>
      {modalGrupo && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: '#0007', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px #0003', padding: 32, minWidth: 340, maxWidth: 480, maxHeight: '80vh', overflowY: 'auto', position: 'relative' }}>
            <button
              onClick={handleCloseModal}
              style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', fontSize: 22, color: '#0a3564', cursor: 'pointer', fontWeight: 700 }}
              aria-label="Cerrar"
            >×</button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 22, marginBottom: 18 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: modalGrupo.color || '#0a3564', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 18, fontWeight: 700, boxShadow: '0 1px 4px #0001', letterSpacing: 0.5 }}>
                {modalGrupo.nombre ? modalGrupo.nombre.trim().charAt(0) : 'G'}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 22, color: '#0a3564', marginBottom: 4, letterSpacing: 0.2 }}>{modalGrupo.nombre}</div>
                <div style={{ fontSize: 15, color: '#888', marginBottom: 8, fontWeight: 500 }}>Alumnos: {modalGrupo.alumnos.length}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
                  {modalGrupo.asesor && (
                    <span style={{ fontSize: 14, color: '#0a3564', background: '#eaf1fb', borderRadius: 10, padding: '5px 14px', fontWeight: 500, boxShadow: '0 1px 4px #0001' }}>
                      <b>Asesor:</b> {modalGrupo.asesor}
                    </span>
                  )}
                  {modalGrupo.modalidad && (
                    <span style={{ fontSize: 14, color: '#0a3564', background: '#f5f7fa', borderRadius: 10, padding: '5px 14px', fontWeight: 500, boxShadow: '0 1px 4px #0001' }}>
                      <b>Modalidad:</b> {modalGrupo.modalidad}
                    </span>
                  )}
                  {modalGrupo.horario && ['Matutino','Vespertino','Nocturno'].includes(modalGrupo.horario) && (
                    <span style={{ fontSize: 14, color: '#0a3564', background: '#f5f7fa', borderRadius: 10, padding: '5px 14px', fontWeight: 500, boxShadow: '0 1px 4px #0001' }}>
                      {modalGrupo.horario}
                    </span>
                  )}
                  {(modalGrupo.horaInicio || modalGrupo.horaFin) && (
                    <span style={{ fontSize: 14, color: '#0a3564', background: '#f5f7fa', borderRadius: 10, padding: '5px 14px', fontWeight: 500, boxShadow: '0 1px 4px #0001' }}>
                      <b>Hora:</b> {modalGrupo.horaInicio || ''}{modalGrupo.horaInicio && modalGrupo.horaFin ? ' - ' : ''}{modalGrupo.horaFin || ''}
                    </span>
                  )}
                  {modalGrupo.aula && (
                    <span style={{ fontSize: 14, color: '#0a3564', background: '#f5f7fa', borderRadius: 10, padding: '5px 14px', fontWeight: 500, boxShadow: '0 1px 4px #0001' }}>
                      <b>Aula:</b> {modalGrupo.aula}
                    </span>
                  )}
                </div>
                {/* Días en círculos */}
                {(() => {
                  const diasSemana = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
                  let diasActivos = [];
                  if (modalGrupo.dias) {
                    const mapDias = {
                      'Domingo': 'D', 'Lunes': 'L', 'Martes': 'M', 'Miércoles': 'M', 'Jueves': 'J', 'Viernes': 'V', 'Sábado': 'S',
                      'D': 'D', 'L': 'L', 'M': 'M', 'Mié': 'M', 'J': 'J', 'V': 'V', 'S': 'S'
                    };
                    diasActivos = modalGrupo.dias.split(/,| /).map(d => mapDias[d.trim()] || d.trim()).filter(Boolean);
                  }
                  return diasActivos.length > 0 ? (
                    <div style={{ display: 'flex', gap: 10, marginTop: 12, marginBottom: 8 }}>
                      {diasSemana.map((dia, idx) => (
                        <span key={dia + idx} style={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          background: diasActivos.includes(dia) ? '#0a3564' : '#eaf1fb',
                          color: diasActivos.includes(dia) ? '#fff' : '#888',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: 12,
                          boxShadow: 'none',
                          transition: 'background 0.2s',
                        }}>{dia}</span>
                      ))}
                    </div>
                  ) : null;
                })()}
              </div>
            </div>
            <div style={{ height: 1, background: '#eaf1fb', margin: '18px 0 16px 0', borderRadius: 2 }} />
            <div style={{ fontSize: 15, fontWeight: 600, color: '#0a3564', marginBottom: 8, letterSpacing: 0.1 }}>Listado de alumnos:</div>
            <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 10 }}>Todos los alumnos:</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {modalGrupo.alumnos.map(alumno => (
                <div key={alumno.id} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f5f7fa', borderRadius: 8, padding: '4px 10px', fontSize: 13 }}>
                  {alumno.avatar ? (
                    <img src={alumno.avatar} alt={alumno.nombre} style={{ width: 22, height: 22, borderRadius: '50%' }} />
                  ) : (
                    <span style={{ fontSize: 16 }}>👤</span>
                  )}
                  <span>{alumno.nombre}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CardGruposEstudiantesModal;
